import { useEffect, useRef } from 'react';
import { readinessFor } from '../../shared/readiness.ts';
import type { LearningSession, LessonContent, StageId } from '../../shared/types.ts';
import { Icon } from './Icon.tsx';
import { CompletionSeal } from './WorkshopProgress.tsx';
import { progressViewFor } from '../lib/workshopProgress.ts';

export function LearningRecord({
  session,
  lesson,
  open,
  onClose,
}: {
  session: LearningSession;
  lesson?: LessonContent;
  open: boolean;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (open) ref.current?.showModal();
    else ref.current?.close();
  }, [open]);
  const readiness = readinessFor(session,lesson);
  const progress = progressViewFor(session,lesson);
  return (
    <dialog
      ref={ref}
      className="record-dialog"
      onCancel={onClose}
      onClose={onClose}
    >
      <div className="panel-heading">
        <div>
          <span className="eyebrow">Your learning record</span>
          <h2>Evidence, with context.</h2>
        </div>
        <button
          className="round"
          aria-label="Close learning record"
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
      </div>
      {progress.award && <div className="earned-record"><CompletionSeal grade={progress.award.grade} small/><p><strong>Workshop completion seal · {progress.award.grade}</strong><br/>{new Date(progress.award.earnedAt).toLocaleDateString()} · {progress.award.score}/100 evidence points when earned.</p></div>}
      <p><strong>{progress.label}</strong> · {progress.score}/100 current evidence points · {progress.tried}/{progress.total} activities tried.</p>
      <p className="note">Evidence points describe the checked lesson rubric. They are separate from a probability of understanding or a school test grade.</p>
      <p>
        <strong>{readiness.label}</strong>
      </p>
      <p>{readiness.note}</p>
      <p className="note">
        Worked lesson:{' '}
        {session.teaching?.completed
          ? 'all parts visited and completion recorded'
          : 'not completed'}
        . This is separate from demonstrated understanding.
      </p>
      <div className="record-stages">
        {(['concept', 'application', 'transfer'] as StageId[]).map((id) => {
          const p = session.stages[id];
          return (
            <section key={id}>
              <span className="eyebrow">{id}</span>
              <h3>{p.complete ? 'Activity complete' : 'Still developing'}</h3>
              <p>
                {p.attempts.filter((a) => a.kind === 'candidate').length} answer
                checks · {p.hintsUsed} hints ·{' '}
                {p.attempts.filter((a) => a.kind === 'tutor').length} tutor
                questions
              </p>
              <small>
                {p.playback.filter((e) => e.event === 'skipped').length}{' '}
                recorded audio skips
              </small>
            </section>
          );
        })}
      </div>
      {!!readiness.evidence.length && (
        <>
          <h3>What we observed</h3>
          <ul>
            {readiness.evidence.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </>
      )}
      {!!readiness.missing.length && (
        <>
          <h3>What would help next</h3>
          <ul>
            {readiness.missing.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </>
      )}
      <p className="note">
        Audio delivery cannot establish attention, listening, or the reason for
        a low score. We do not infer a readiness percentage from this small
        sample.
      </p>
      <a
        className="secondary"
        href={`/api/sessions/${session.id}/export`}
        download
      >
        Export learning record
      </a>
      <p className="microcopy">
        The public HTTPS preview keeps records private to this browser. People
        using this same browser can access them. The local studio is a
        shared-device tool. Family accounts and parent consent are still being
        developed.
      </p>
    </dialog>
  );
}
